import { useCallback, useMemo } from 'react'
import { SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { CompositeSessionFilter } from '../../../../components/SessionFilters/model'
import { useGetRoomSessionsForFilterQuery } from '../../../../apps/professional-app/fa/store/fa'
import { parseMetadata } from '../../../../common/util'
import { RoomSession } from '../../../../models/Room'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'

export interface MeetingDropdownProps {
  sessionFilter: CompositeSessionFilter
  onSessionSelected: (session: RoomSession) => void
  initialSessionOption?: SelectOption | null
}

export const MeetingDropdown: React.FC<MeetingDropdownProps> = ({ sessionFilter, onSessionSelected, initialSessionOption = null }) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    isLoading: gettingRoomSessions,
    data: roomSessions,
    isFetching: getRoomSessionsIsFetching,
  } = useGetRoomSessionsForFilterQuery({ accountId, filter: sessionFilter, pageRequest: { page: 0, size: 20 } }, { skip: !accountId })

  const sessionOptions = useMemo(() => {
    return [...(roomSessions?.data ?? [])]
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
      .map((item) => ({
        id: item.id,
        label: parseMetadata(item.metadata)?.external?.title ?? item?.startedAt ?? 'Meeting',
      }))
  }, [roomSessions])

  const handleSessionSelected = useCallback(
    (option: SelectOption) => {
      const session = (roomSessions?.data ?? []).find((s) => s.id === option.id)
      if (session) {
        onSessionSelected(session)
      }
    },
    [roomSessions, onSessionSelected],
  )

  return (
    <SelectDropdown
      options={sessionOptions || []}
      initialSelectedOption={initialSessionOption}
      placeholder='Select Meeting'
      searchable
      onOptionSelected={handleSessionSelected}
      loading={gettingRoomSessions || getRoomSessionsIsFetching}
    />
  )
}
